import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom';
import { numberOfTriesList, taskFeatureList } from '../../../../../utils/constants';
import InputWithLabel from '../../../../../component/common/InputWithLabel';
import { Tooltip } from 'react-tooltip';
import Dots from '../../../../../assets/images/Dots.svg';
import ActiveDots from '../../../../../assets/images/active-dots.svg';
import warning from '../../../../../assets/svg/icons/Warning.svg';
import drag from '../../../../../assets/svg/icons/Drag.svg';
import FormSelect from '../../../../../component/common/FormSelect';
import toast from 'react-hot-toast';
import RightArrow from '../../../../../assets/images/RightArrow.svg';
import DraftPopup from '../../../../../component/common/modals/DraftPopup';
import { capitalizeFirstLetter, getLocalStorageItem } from '../../../../../utils/helper';

const initialTaskFormData = {
    id: '',
    feature: 'Wearable Device', // Default to Wearable Device for B2B companies
    title: '',
    description: '',
    number_of_tries: '',
    connect_to_wearable: '',
    duration: '0',
    isSubscription: "",
    hours: "00",
    mins: "00",
    days: '0',
    action: 'complete', // create | complete — only meaningful for Goals/Cleanse
    subFeature: '', // Breathwork only: BREATH_HOLD | BREATH_EXHALE | BREATHWORK_AUDIO
    goal_mode: 'window', // window = "N within D days" | daily = "N per day for D days"
    // Wearable specific fields - Pre-filled with steps & daily tracking
    wearableConfig: {
        dataType: 'steps', // Default to steps
        targetValue: '',
        unit: 'steps', // Default unit for steps
        trackingPeriod: 'daily', // Default to daily
        aggregationType: 'sum'
    }
};

const connectToWearable = [
    { id: 1, name: "Connect", value: 1 },
    { id: 2, name: 'Not Connect', value: 0 },
]

// Features with a create→complete lifecycle. For these the admin can choose
// which event the task tracks; every other feature is a single event.
const DUAL_ACTION_FEATURES = ['Goals', 'Cleanse'];
const taskActions = [
    { name: 'Complete', value: 'complete' },
    { name: 'Create', value: 'create' },
]

const SubscriptionStatus = [
    { name: 'Yes', value: 1 },
    { name: 'No', value: 0 }
];

// Breathwork has three distinct trackable activities; a badge task must pick
// exactly one so "2 Hold sessions" and "2 audios" progress independently.
const BREATHWORK_FEATURE = 'Breathwork';

// The backend stores subFeature as an array (['BREATH_HOLD']); the form works
// with a single string. Normalize whichever shape a saved task carries.
const subFeatureValue = (sf) => (Array.isArray(sf) ? sf[0] || '' : sf || '');
const breathworkSessionTypes = [
    { name: 'Breath Hold (session)', value: 'BREATH_HOLD' },
    { name: 'Breath Exhale (session)', value: 'BREATH_EXHALE' },
    { name: 'Breathwork Audio (listen)', value: 'BREATHWORK_AUDIO' },
];

// How the target is measured. "window" = N total within D days (existing
// engine behaviour); "daily" = N every day for D consecutive days.
const goalModeOptions = [
    { name: 'Total within window (N in D days)', value: 'window' },
    { name: 'Per day, consecutive (N daily for D days)', value: 'daily' },
];

// Wearable device options
const wearableDataTypes = [
    { name: 'Steps', value: 'steps' },
    { name: 'Heart Rate', value: 'heart_rate' },
    { name: 'Calories', value: 'calories' },
    { name: 'Distance', value: 'distance' },
    { name: 'Sleep Duration', value: 'sleep_duration' },
    { name: 'Active Minutes', value: 'active_minutes' }
];

const trackingPeriods = [
    { name: 'Daily', value: 'daily' },
    { name: 'Weekly', value: 'weekly' },
    { name: 'Total Challenge', value: 'total' }
];

const aggregationTypes = [
    { name: 'Sum', value: 'sum' },
    { name: 'Average', value: 'average' },
    { name: 'Maximum', value: 'max' },
    { name: 'Minimum', value: 'min' }
];

// Unit options based on data type
const getUnitOptions = (dataType) => {
    switch (dataType) {
        case 'steps':
            return [{ name: 'Steps', value: 'steps' }];
        case 'heart_rate':
            return [{ name: 'BPM', value: 'bpm' }, { name: 'Beats per minute', value: 'beats/min' }];
        case 'calories':
            return [{ name: 'Calories', value: 'kcal' }, { name: 'Kilojoules', value: 'kJ' }];
        case 'distance':
            return [
                { name: 'Kilometers', value: 'km' },
                { name: 'Miles', value: 'miles' },
                { name: 'Meters', value: 'm' }
            ];
        case 'sleep_duration':
            return [
                { name: 'Hours', value: 'hours' },
                { name: 'Minutes', value: 'minutes' }
            ];
        case 'active_minutes':
            return [{ name: 'Minutes', value: 'minutes' }];
        default:
            return [
                { name: 'Steps', value: 'steps' },
                { name: 'BPM', value: 'bpm' },
                { name: 'Calories', value: 'kcal' },
                { name: 'Kilometers', value: 'km' },
                { name: 'Hours', value: 'hours' },
                { name: 'Minutes', value: 'minutes' }
            ];
    }
};

const hourstList = Array.from({ length: 24 }, (_, i) => {
    const val = i.toString().padStart(2, '0');
    return { name: val, value: val };
});

const minsList = Array.from({ length: 60 }, (_, i) => {
    const val = i.toString().padStart(2, '0');
    return { name: val, value: val };
});

const durationInDays = [
    { value: 'App Consistency' },
    { value: 'Meditations' },
    { value: 'Shoorah Pods' },
    { value: 'Breathwork' },
    { value: 'Sleep Sounds' },
    { value: 'Guidance' },
    { value: 'Health Videos' },
    { value: 'Shoorah Vids' },
    { value: 'Goals' },
    { value: 'Cleanse' },
    { value: 'Rituals' },
    { value: 'Gratitudes' },
    { value: 'Affirmations' },
    { value: 'Daily journal' },
    { value: 'Vision Board' },
];

const noOfTrys = [
    { value: 'Meditations' },
    { value: 'Shoorah Pods' },
    { value: 'Breathwork' },
    { value: 'Sleep Sounds' },
    { value: 'Guidance' },
    { value: 'Health Videos' },
    { value: 'Shoorah Vids' },
    { value: 'Goals' },
    { value: 'Cleanse' },
    { value: 'Rituals' },
    { value: 'Gratitudes' },
    { value: 'Affirmations' },
    { value: 'Daily journal' },
    { value: 'Vision Board' },
    { value: 'Shoorah Test' },
    { value: 'Session In Peap' },
    { value: 'Shuru Chat Boat' },
    { value: 'Marketplace' },
    { value: 'Health Kit' },
    { value: 'Game' },
]

const durationInHrMins = [
    { value: 'App Consistency' },
]

const BadgeTaskForm = ({ data, setData, handleFinalSubmit, challange = false, setNewChallenge, newChallenge }) => {
    const [searchParams] = useSearchParams();
    const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));
    const queryparams = Object.fromEntries(searchParams);
    const view = queryparams.view || 'edit';

    const [draftPopUpOpen, setDraftPopUpOpen] = useState(false);

    const handleDraftPopUp = () => {
        setDraftPopUpOpen(false);
    }

    const TaskCard = ({ task, onDelete, onEdit, handleDragStart, handleDragEnter, handleDragEnd }) => {
        const [cardId2, setCardId2] = useState(null);
        const popupRef2 = useRef(null);

        const handleButtonClick2 = (feature) => {
            setCardId2(prev => prev === feature ? null : feature);
        };

        useEffect(() => {
            const handleClickOutside = (event) => {
                if (popupRef2.current && !popupRef2.current.contains(event.target)) {
                    setCardId2(null);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, []);

        return (
            <div className="bg-white gap-4 rounded-xl shadow-sm border border-gray-200 mb-4 p-3">
                <div className="flex gap-4">
                    <div
                        className="flex items-center justify-center min-w-[max-content]"
                        draggable
                        onDragStart={handleDragStart}
                        onDragEnter={handleDragEnter}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => e.preventDefault()}
                    >
                        <img src={drag} alt="Drag" className="w-6 h-6 cursor-move" />
                    </div>

                    <div className="flex-1">
                        <div className="flex justify-between items-start mb-3">
                            <h2 className="font-medium flex-wrap text-gray-800 flex items-center gap-2">
                                {task.grouped_by}
                            </h2>
                        </div>

                        {task?.sub_tasks?.map((t) => (
                            <div
                                key={t.id}
                                className="bg-[#F4F5F6] px-4 py-3 rounded-lg mb-2 border border-gray-100 flex justify-between items-start"
                            >
                                <div>
                                    <p className="font-medium text-gray-800">{t.title}</p>
                                    <p className="text-sm text-gray-500">{t.description}</p>
                                </div>
                                <div className="relative" >
                                    <button onClick={() => handleButtonClick2(t.feature)}>
                                        <img src={Dots} alt="Menu" className="w-7 h-6 block dark:hidden" />
                                        <img src={ActiveDots} alt="Menu Active" className="w-6 h-6 hidden dark:block" />
                                    </button>

                                    {cardId2 === t.feature && (
                                        <div ref={popupRef2}
                                            className="w-35 z-50 bg-white absolute top-4 right-0 mt-2 border-[1.5px] rounded-lg shadow-lg"
                                        >
                                            <div
                                                className="py-2 px-5 hover:bg-gray-100 cursor-pointer text-black font-medium"
                                                onClick={() => {
                                                    onEdit(task.grouped_by, t);
                                                    setCardId2(null);
                                                }}
                                            >
                                                Edit
                                            </div>
                                            <div className="border-b border-gray-400 w-full" />
                                            <div
                                                className="py-2 px-5 hover:bg-gray-100 cursor-pointer text-red-700 font-medium"
                                                onClick={() => {
                                                    onDelete(task.grouped_by, t);
                                                    setCardId2(null);
                                                }}
                                            >
                                                Delete
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const DraggableCardGrid = ({ tasks, setTasks, setFormData, onEdit, onDelete }) => {
        const dragCardIndex = useRef(null);
        const dragOverCardIndex = useRef(null);

        const handleDragStart = (index) => {
            dragCardIndex.current = index;
        };

        const handleDragEnter = (index) => {
            dragOverCardIndex.current = index;
        };

        const handleDragEnd = () => {
            const updatedCards = [...tasks];
            const draggedCard = updatedCards[dragCardIndex.current];
            updatedCards.splice(dragCardIndex.current, 1);
            updatedCards.splice(dragOverCardIndex.current, 0, draggedCard);
            setTasks(updatedCards);
            dragCardIndex.current = null;
            dragOverCardIndex.current = null;
        };

        return (
            <div className="grid grid-cols-1 gap-4">
                <div className="grid grid-cols-1 max-w-[900px]">
                    {tasks.map((task, index) => (
                        <TaskCard
                            key={task.id}
                            task={task}
                            tasks={tasks}
                            setTasks={setTasks}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            setFormData={setFormData}
                            handleDragStart={() => handleDragStart(index)}
                            handleDragEnter={() => handleDragEnter(index)}
                            handleDragEnd={handleDragEnd}
                        />
                    ))}
                </div>
            </div>
        );
    };

    const TaskForm = () => {
        const [tasks, setTasks] = useState(data?.tasks || []);
        const [formData, setFormData] = useState(initialTaskFormData);
        const [editTaskId, setEditTaskId] = useState(null);
        const formRef = useRef(null);
        const location = useLocation();

        useEffect(() => {
            const queryParams = new URLSearchParams(location.search);
            const newChallengeParam = queryParams.get("newchallenge");
            // Only the Challenges flow passes this setter; the Badges flow does not.
            setNewChallenge?.(newChallengeParam);
        }, [location.search]);

        const radioHandler = (e) => {
            let { name, value } = e.target;
            setFormData((prevData) => ({
                ...prevData,
                [name]: parseInt(value)
            }));
        };

        const handleDelete = (grouped_by, subTask) => {
            const updatedTasks = tasks
                .map(task => {
                    if (task.grouped_by === grouped_by) {
                        const updatedSubTasks = task.sub_tasks.filter(st => st.id !== subTask.id);
                        return { ...task, sub_tasks: updatedSubTasks };
                    }
                    return task;
                })
                .filter(task => task.sub_tasks.length > 0);

            setTasks(updatedTasks);
            toast.success('Task deleted');
            setFormData(initialTaskFormData);
            setEditTaskId(null);
        };

        const handleEdit = (grouped_by, subTask) => {
            if (subTask) {
                // Rebuild form fields from the saved task. `duration` is stored
                // as total minutes; split it back into the hours/mins selects.
                const totalMinutes = Number(subTask.duration) || 0;
                const derivedHours = Math.floor(totalMinutes / 60)
                    .toString()
                    .padStart(2, '0');
                const derivedMins = (totalMinutes % 60).toString().padStart(2, '0');
                // isSubscription is a boolean on the backend but the radios use 1/0.
                const normalizedSubscription =
                    typeof subTask.isSubscription === 'boolean'
                        ? (subTask.isSubscription ? 1 : 0)
                        : subTask.isSubscription;
                setFormData({
                    ...subTask,
                    subFeature: subFeatureValue(subTask.subFeature),
                    // Saved tasks carry daily_target (boolean); the form works
                    // with the friendlier goal_mode select.
                    goal_mode: subTask.goal_mode || (subTask.daily_target ? 'daily' : 'window'),
                    days: subTask.days != null ? String(subTask.days) : '0',
                    hours: subTask.hours ?? derivedHours,
                    mins: subTask.mins ?? derivedMins,
                    isSubscription: normalizedSubscription,
                    connect_to_wearable:
                        typeof subTask.connect_to_wearable === 'boolean'
                            ? (subTask.connect_to_wearable ? 1 : 0)
                            : subTask.connect_to_wearable,
                    wearableConfig: subTask.wearableConfig || {
                        dataType: '',
                        targetValue: '',
                        unit: '',
                        trackingPeriod: 'daily',
                        aggregationType: 'sum'
                    }
                });
                setEditTaskId({ grouped_by, id: subTask.id });

                setTimeout(() => {
                    formRef.current?.scrollIntoView({ behavior: 'smooth' });
                }, 0);
            }
        };

        useEffect(() => {
            if (JSON.stringify(data.tasks) !== JSON.stringify(tasks)) {
                setData((prev) => ({ ...prev, tasks }));
            }
        }, [tasks]);

        const handleChange = (e) => {
            const { name, value: rawValue } = e.target;
            const value = capitalizeFirstLetter(rawValue);
            setFormData((prev) => ({ ...prev, [name]: value }));
        };

        const handleChange2 = (name, value) => {
            setFormData((prev) => ({
                ...prev,
                [name]: value
            }));
        };

        // Handle wearable config changes
        const handleWearableConfigChange = (name, value) => {
            setFormData((prev) => {
                const updatedConfig = {
                    ...prev.wearableConfig,
                    [name]: value
                };

                // Auto-select appropriate unit when data type changes
                if (name === 'dataType') {
                    const unitOptions = getUnitOptions(value);
                    if (unitOptions.length > 0) {
                        updatedConfig.unit = unitOptions[0].value;
                    }
                }

                return {
                    ...prev,
                    wearableConfig: updatedConfig
                };
            });
        };

        // Auto-set connect_to_wearable when feature changes
        useEffect(() => {
            if (formData.feature === 'Wearable Device') {
                setFormData((prev) => ({
                    ...prev,
                    connect_to_wearable: 1 // Auto-set to true for wearable tasks
                }));
            }
            // Session type only applies to Breathwork; drop stale values.
            if (formData.feature !== BREATHWORK_FEATURE && formData.subFeature) {
                setFormData((prev) => ({ ...prev, subFeature: '' }));
            }
        }, [formData.feature]);

        const handleSubmit = async (e) => {
            e.preventDefault();
            const { feature, title, description, number_of_tries, connect_to_wearable, days, hours, mins, isSubscription, wearableConfig } = formData;

            if (!title) return toast.error('Title is required');
            if (!feature) return toast.error('Task feature is required');
            if (!description) return toast.error('Task description is required');

            // Additional validation for wearable tasks
            if (feature === 'Wearable Device') {
                if (!wearableConfig.dataType) return toast.error('Wearable data type is required');
                if (!wearableConfig.targetValue) return toast.error('Target value is required');
                if (!wearableConfig.unit) return toast.error('Unit is required');
            }

            // Breathwork tasks must state which activity they track.
            if (feature === BREATHWORK_FEATURE && !formData.subFeature) {
                return toast.error('Session type is required for Breathwork tasks');
            }
            // "N per day for D days" needs both the per-day count and the days.
            if (formData.goal_mode === 'daily') {
                if (!number_of_tries || Number(number_of_tries) < 1)
                    return toast.error('Per-day count is required for a daily goal');
                if (!days || Number(days) < 1)
                    return toast.error('Number of days is required for a daily goal');
            }

            const selectedFeature = taskFeatureList.find(item => item.value === feature);
            if (!selectedFeature) return toast.error('Selected feature is invalid');

            const grouped_by = selectedFeature.groubedBy;
            const msg = selectedFeature.msg;

            if (editTaskId) {
                const currentTask = tasks.find(task => task.grouped_by === editTaskId.grouped_by);
                const currentSubTask = currentTask?.sub_tasks.find(sub => sub.id === editTaskId.id);
                const featureChanged = currentSubTask?.feature !== feature;

                if (featureChanged) {
                    // Allow multiple wearable device tasks, but prevent duplicates
                    // for other features. Dual-action features (Goals/Cleanse) may
                    // have one task per action, so include the action in the check.
                    if (feature !== 'Wearable Device') {
                        const isDual = DUAL_ACTION_FEATURES.includes(feature);
                        const isBreathwork = feature === BREATHWORK_FEATURE;
                        const isFeatureAlreadyUsed = tasks.some(task =>
                            task.sub_tasks.some(sub =>
                                sub.feature === feature &&
                                sub.id !== editTaskId.id &&
                                (!isDual || (sub.action || 'complete') === formData.action) &&
                                // Breathwork allows one task per session type.
                                (!isBreathwork || subFeatureValue(sub.subFeature) === formData.subFeature)
                            )
                        );
                        if (isFeatureAlreadyUsed) {
                            toast.error(
                                isDual
                                    ? `Selected feature "${feature}" already has a "${formData.action}" task.`
                                    : isBreathwork
                                        ? `Breathwork already has a task for this session type.`
                                        : `Selected feature "${feature}" already has a task.`
                            );
                            return;
                        }
                    }

                    const updatedTasksAfterRemoval = tasks.map(task => {
                        if (task.grouped_by === editTaskId.grouped_by) {
                            const updatedSubTasks = task.sub_tasks.filter(sub => sub.id !== editTaskId.id);
                            return { ...task, sub_tasks: updatedSubTasks };
                        }
                        return task;
                    }).filter(task => task.sub_tasks.length > 0);

                    const updatedSubTask = {
                        ...currentSubTask,
                        ...formData,
                        backend_name: selectedFeature?.backendName,
                        subFeature:
                            feature === BREATHWORK_FEATURE && formData.subFeature
                                ? [formData.subFeature]
                                : [],
                        daily_target: formData.goal_mode === 'daily',
                        // Include wearableConfig only for wearable tasks
                        ...(feature === 'Wearable Device' && { wearableConfig })
                    };

                    const existingNewGroup = updatedTasksAfterRemoval.find(task => task.grouped_by === grouped_by);

                    let finalUpdatedTasks;
                    if (existingNewGroup) {
                        finalUpdatedTasks = updatedTasksAfterRemoval.map(task =>
                            task.grouped_by === grouped_by
                                ? { ...task, sub_tasks: [...task.sub_tasks, updatedSubTask] }
                                : task
                        );
                        toast.success(`Task moved to '${grouped_by}' group and updated!`);
                    } else {
                        const newTaskGroup = {
                            id: `tooltip-${grouped_by.replace(/\s+/g, '-').toLowerCase()}-${Math.random(90000, 99999).toFixed()}`,
                            grouped_by,
                            msg,
                            sub_tasks: [updatedSubTask],
                        };
                        finalUpdatedTasks = [...updatedTasksAfterRemoval, newTaskGroup];
                        toast.success(`Task moved to new group '${grouped_by}' and updated!`);
                    }
                    setTasks(finalUpdatedTasks);
                } else {
                    const updatedTasks = tasks.map(task => {
                        if (task.grouped_by === editTaskId.grouped_by) {
                            const updatedSubTasks = task.sub_tasks.map((sub) =>
                                sub.id === editTaskId.id ? {
                                    ...sub,
                                    ...formData,
                                    backend_name: selectedFeature?.backendName,
                                    subFeature:
                                        feature === BREATHWORK_FEATURE && formData.subFeature
                                            ? [formData.subFeature]
                                            : [],
                                    daily_target: formData.goal_mode === 'daily',
                                    // Include wearableConfig only for wearable tasks
                                    ...(feature === 'Wearable Device' && { wearableConfig })
                                } : sub
                            );
                            return { ...task, sub_tasks: updatedSubTasks };
                        }
                        return task;
                    });
                    setTasks(updatedTasks);
                    toast.success('Task updated');
                }
                setEditTaskId(null);
                setFormData(initialTaskFormData);
                return;
            }

            // Allow multiple wearable device tasks, but prevent duplicates for
            // other features. Dual-action features (Goals/Cleanse) may have one
            // task per action, so the duplicate check includes the action.
            if (feature !== 'Wearable Device') {
                const isDual = DUAL_ACTION_FEATURES.includes(feature);
                const isBreathwork = feature === BREATHWORK_FEATURE;
                const isFeatureAlreadyUsed = tasks.some(task =>
                    task.sub_tasks.some(sub =>
                        sub.feature === feature &&
                        (!isDual || (sub.action || 'complete') === formData.action) &&
                        // Breathwork allows one task per session type.
                        (!isBreathwork || subFeatureValue(sub.subFeature) === formData.subFeature)
                    )
                );
                if (isFeatureAlreadyUsed) {
                    toast.error(
                        isDual
                            ? `Selected feature "${feature}" already has a "${formData.action}" task.`
                            : isBreathwork
                                ? `Breathwork already has a task for this session type.`
                                : `Selected feature "${feature}" already has a task.`
                    );
                    return;
                }
            }

            const existingTaskGroup = tasks.find(task => task.grouped_by === grouped_by);
            const newSubTask = {
                id: "",
                title,
                feature: feature,
                backend_name: selectedFeature?.backendName,
                description,
                number_of_tries,
                days,
                hours,
                mins,
                isSubscription,
                connect_to_wearable,
                // Only dual-action features carry a chosen action; others default.
                action: DUAL_ACTION_FEATURES.includes(feature)
                    ? formData.action
                    : 'complete',
                // Breathwork carries its session type; daily_target flags the
                // "N per day for D consecutive days" mode for the engine.
                subFeature:
                    feature === BREATHWORK_FEATURE && formData.subFeature
                        ? [formData.subFeature]
                        : [],
                daily_target: formData.goal_mode === 'daily',
                status: "draft",
                // Include wearableConfig only for wearable tasks
                ...(feature === 'Wearable Device' && { wearableConfig })
            };

            if (existingTaskGroup) {
                const updatedTasks = tasks.map(task =>
                    task.grouped_by === grouped_by
                        ? { ...task, sub_tasks: [...task.sub_tasks, newSubTask] }
                        : task
                );

                setTasks(updatedTasks);
                toast.success(`Task added under '${grouped_by}' group!`);
            } else {
                const newTask = {
                    id: `tooltip-${grouped_by.replace(/\s+/g, '-').toLowerCase()}-${Math.random(90000, 99999).toFixed()}`,
                    grouped_by,
                    msg,
                    sub_tasks: [newSubTask],
                };

                setTasks(prev => [...prev, newTask]);
                toast.success(`New task group '${grouped_by}' created!`);
            }

            setFormData(initialTaskFormData);
        };

        const isWearableTask = formData.feature === 'Wearable Device';

        return (
            <div>
                {tasks?.length > 0 && (
                    <div className="h-full mt-[10px]">
                        <DraggableCardGrid
                            setFormData={setFormData}
                            onEdit={handleEdit}
                            tasks={tasks}
                            setTasks={setTasks}
                            onDelete={handleDelete}
                        />
                    </div>
                )}
                <div
                    ref={formRef}
                    className="h-full p-6 bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray lg:rounded-2xl rounded-xl mt-[15px]"
                >
                    <div className="h-full flex flex-col sm:flex-row items-start md:gap-4 gap-2">
                        <div className="space-y-4 max-w-[846px] w-full">
                            <form onSubmit={handleSubmit}>
                                <InputWithLabel
                                    label={'Task Title'}
                                    placeholder={'Task Title'}
                                    containerClassName={'w-full'}
                                    onChange={handleChange}
                                    value={formData?.title}
                                    name={'title'}
                                    disabled={view === 'preview' || view === 'preview_only'}
                                />
                                <div className="text-[16px] text-shoorah-lightBlack mt-3 sm:w-full max-w-[846px] dark:text-shoorah-darkBg">
                                    <label
                                        htmlFor="description"
                                        className="font-medium text-[16px] leading-9 mb-1"
                                    >
                                        Task Description
                                    </label>
                                    <div className="input-field">
                                        <textarea
                                            className="w-full block rounded-lg px-4 py-2 text-[14px] bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray min-h-[100px] resize-none mb-0 dark:text-shoorah-darkBg dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none"
                                            value={formData?.description}
                                            name="description"
                                            id="description"
                                            placeholder="Enter task description"
                                            onChange={handleChange}
                                            maxLength={500}
                                            disabled={view === 'preview' || view === 'preview_only'}
                                        ></textarea>
                                    </div>
                                </div>

                                {/* Task Feature Selection */}
                                <div className="flex flex-col sm:flex-row justify-between gap-5 mt-3 w-full h-full items-center">
                                    <div className="flex w-full flex-col min-w-[150px]">
                                        <FormSelect
                                            key={formData.feature}
                                            label="Task Feature"
                                            options={taskFeatureList}
                                            name="feature"
                                            placeholder="Task Feature"
                                            onChange={handleChange2}
                                            value={formData?.feature || 'Wearable Device'}
                                            disabled={view === 'preview' || view === 'preview_only'}
                                        />
                                    </div>
                                    {/* Action: only for features with a create→complete lifecycle */}
                                    {DUAL_ACTION_FEATURES.includes(formData?.feature) && (
                                        <div className="flex w-full flex-col min-w-[150px]">
                                            <FormSelect
                                                key={`action-${formData.action}`}
                                                label="Action"
                                                options={taskActions}
                                                name="action"
                                                placeholder="Action"
                                                onChange={handleChange2}
                                                value={formData?.action || 'complete'}
                                                disabled={view === 'preview' || view === 'preview_only'}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Breathwork: which activity the task tracks + how the target is measured */}
                                {formData?.feature === BREATHWORK_FEATURE && (
                                    <div className="flex flex-col sm:flex-row justify-between gap-5 mt-3 w-full h-full items-center">
                                        <div className="flex w-full flex-col min-w-[150px]">
                                            <FormSelect
                                                key={`subFeature-${formData.subFeature}`}
                                                label="Session Type"
                                                options={breathworkSessionTypes}
                                                name="subFeature"
                                                placeholder="Select session type"
                                                onChange={handleChange2}
                                                value={formData?.subFeature || ''}
                                                disabled={view === 'preview' || view === 'preview_only'}
                                            />
                                        </div>
                                        <div className="flex w-full flex-col min-w-[150px]">
                                            <FormSelect
                                                key={`goal-${formData.goal_mode}`}
                                                label="Goal Mode"
                                                options={goalModeOptions}
                                                name="goal_mode"
                                                placeholder="Goal mode"
                                                onChange={handleChange2}
                                                value={formData?.goal_mode || 'window'}
                                                disabled={view === 'preview' || view === 'preview_only'}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Wearable Device Configuration - Only show if feature is Wearable Device */}
                                {isWearableTask && (
                                    <div className="mt-3">
                                        <h3 className="font-medium text-[16px] leading-9 mb-3 text-shoorah-lightBlack dark:text-shoorah-darkBg">Wearable Device Configuration</h3>
                                        <div className="flex flex-col sm:flex-row justify-between gap-5 mt-3 w-full h-full items-center">
                                            <div className="flex w-full flex-col min-w-[150px]">
                                                <FormSelect
                                                    label="Data Type"
                                                    options={wearableDataTypes}
                                                    name="dataType"
                                                    placeholder="Select data type"
                                                    onChange={(name, value) => handleWearableConfigChange(name, value)}
                                                    value={formData?.wearableConfig?.dataType || 'steps'}
                                                    disabled={true} // Always disabled - only steps allowed for now
                                                />
                                            </div>
                                            <div className="flex w-full flex-col min-w-[150px]">
                                                <InputWithLabel
                                                    label="Target Value"
                                                    placeholder="Enter target value"
                                                    containerClassName="w-full"
                                                    type="number"
                                                    onChange={(e) => handleWearableConfigChange('targetValue', e.target.value)}
                                                    value={formData?.wearableConfig?.targetValue || ''}
                                                    name="targetValue"
                                                    disabled={view === 'preview' || view === 'preview_only'}
                                                />
                                            </div>
                                            <div className="flex w-full flex-col min-w-[150px]">
                                                <FormSelect
                                                    label="Unit"
                                                    options={getUnitOptions(formData?.wearableConfig?.dataType)}
                                                    name="unit"
                                                    placeholder="Select unit"
                                                    onChange={(name, value) => handleWearableConfigChange(name, value)}
                                                    value={formData?.wearableConfig?.unit || 'steps'}
                                                    disabled={true} // Auto-selected based on data type
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-col sm:flex-row justify-between gap-5 mt-3 w-full h-full items-center">
                                            <div className="flex w-full flex-col min-w-[150px]">
                                                <FormSelect
                                                    label="Tracking Period"
                                                    options={trackingPeriods}
                                                    name="trackingPeriod"
                                                    placeholder="Select tracking period"
                                                    onChange={(name, value) => handleWearableConfigChange(name, value)}
                                                    value={formData?.wearableConfig?.trackingPeriod || 'daily'}
                                                    disabled={true} // Always disabled - only daily allowed for now
                                                />
                                            </div>
                                            <div className="flex w-full flex-col min-w-[150px]">
                                                <FormSelect
                                                    label="Aggregation Type"
                                                    options={aggregationTypes}
                                                    name="aggregationType"
                                                    placeholder="Select aggregation type"
                                                    onChange={(name, value) => handleWearableConfigChange(name, value)}
                                                    value={formData?.wearableConfig?.aggregationType || 'sum'}
                                                    disabled={view === 'preview' || view === 'preview_only'}
                                                />
                                            </div>
                                            <div className="flex w-full flex-col min-w-[150px]">
                                                {/* Empty div for alignment */}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Other form fields */}
                                <div className="flex flex-col sm:flex-row justify-between gap-5 mt-3 w-full h-full items-center">
                                    {(noOfTrys.some(item => item.value === formData?.feature)) &&
                                        <div className="flex w-full flex-col min-w-[150px]">
                                            <FormSelect
                                                key={formData.number_of_tries}
                                                label={formData?.goal_mode === 'daily' ? 'Count per day' : 'Number of tries'}
                                                placeholder={formData?.goal_mode === 'daily' ? 'Count per day' : 'Number of tries'}
                                                options={numberOfTriesList}
                                                name="number_of_tries"
                                                onChange={handleChange2}
                                                value={formData?.number_of_tries || ''}
                                                disabled={view === 'preview' || view === 'preview_only'}
                                            />
                                        </div>
                                    }
                                    {(durationInDays.some(item => item.value === formData?.feature)) &&
                                        <div className="flex w-full flex-col min-w-[150px]">
                                            <InputWithLabel
                                                label={formData?.goal_mode === 'daily' ? 'For (Consecutive Days)' : 'Limit (Days)'}
                                                placeholder={formData?.goal_mode === 'daily' ? 'Days' : 'Limit'}
                                                containerClassName="w-full"
                                                type="number"
                                                onChange={handleChange}
                                                value={formData?.days}
                                                onWheel={(e) => e.currentTarget.blur()}
                                                labelMargin={'mb-5'}
                                                name="days"
                                                disabled={view === 'preview' || view === 'preview_only'}
                                            />
                                        </div>}

                                    {/* Only show connect to wearable option for non-wearable tasks and specific conditions */}
                                    {(challange && !isWearableTask && (formData?.feature === taskFeatureList[20]?.value)) && (
                                        <div className="flex w-full flex-col min-w-[150px]">
                                            <FormSelect
                                                key={formData.connect_to_wearable}
                                                label="Connect to wearable"
                                                options={connectToWearable}
                                                name="connect_to_wearable"
                                                onChange={handleChange2}
                                                value={formData?.connect_to_wearable || ''}
                                                disabled={view === 'preview' || view === 'preview_only'}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col sm:flex-row justify-start gap-5 mt-3 w-full h-full items-center">
                                    {(formData?.feature === taskFeatureList[0]?.value) &&
                                        <div className="flex w-full flex-col min-w-[200px] max-w-[350px]">
                                            <fieldset className="mt-2">
                                                <label
                                                    htmlFor="isSubscription"
                                                    className="font-medium text-[16px] leading-9 mb-5"
                                                >
                                                    Subscription required?
                                                </label>
                                                <div className="flex space-x-4 min-w-[150px]">
                                                    {SubscriptionStatus.map((status, index) => (
                                                        <div key={index} className="flex items-center">
                                                            <input
                                                                id={status.name}
                                                                name="isSubscription"
                                                                type="radio"
                                                                value={status.value}
                                                                onChange={(e) => radioHandler(e)}
                                                                checked={status.value === formData.isSubscription}
                                                                className="h-5 w-5 border-gray-300 text-shoorah-primary focus:ring-shoorah-primary cursor-pointer"
                                                            />
                                                            <label
                                                                htmlFor={status.name}
                                                                className="ml-3 w-fit block text-sm font-medium text-gray-700 dark:text-white"
                                                            >
                                                                {status.name}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </fieldset>
                                        </div>
                                    }
                                    {(durationInHrMins.some(item => item.value === formData?.feature)) && <>
                                        <div className="flex w-full flex-col min-w-[150px]">
                                            <FormSelect
                                                key={formData.hours}
                                                label="Duration (Hours)"
                                                placeholder="Hours"
                                                options={hourstList}
                                                name="hours"
                                                onChange={handleChange2}
                                                value={formData?.hours || ''}
                                                disabled={view === 'preview' || view === 'preview_only'}
                                            />
                                        </div>
                                        <div className="flex w-full flex-col min-w-[150px]">
                                            <FormSelect
                                                key={formData.mins}
                                                label="Duration (Mins)"
                                                placeholder="Mins"
                                                options={minsList}
                                                name="mins"
                                                onChange={handleChange2}
                                                value={formData?.mins || ''}
                                                disabled={view === 'preview' || view === 'preview_only'}
                                            />
                                        </div>
                                    </>}
                                </div>

                                <div className="flex flex-row w-full h-full justify-start mt-3">
                                    <button
                                        type="submit"
                                        className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-2 rounded-full transition-all 
                                 flex items-center gap-2"
                                    >
                                        Save task
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
                <div className="flex justify-end mt-3 gap-3">
                    {
                        <button
                            onClick={handleFinalSubmit}
                            type="button"
                            className="btn-primary btn-sm w-full sm:w-auto"
                        >
                            {challange ? 'Save & Continue' : 'Preview'}
                            <img className="ml-2" src={RightArrow} alt="edit" />
                        </button>
                    }
                </div>
                {draftPopUpOpen && (
                    <DraftPopup
                        open={draftPopUpOpen}
                        setOpen={setDraftPopUpOpen}
                        message={'You could save your progress and continue at a later date'}
                        setAccepted={(e) => handleDraftPopUp()}
                    />
                )}
            </div>
        );
    };

    return (
        <div>
            <TaskForm />
        </div>
    )
}

export default BadgeTaskForm